<template>
    <div id="app" data-app="true">
      <!-- Global App Loader -->
      <AppLoader :isLoading="isAppLoading" />
      
      <router-view></router-view>
    </div>
</template>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

$gmf-font: "Inter", ui-sans-serif, system-ui, sans-serif;

html,
body,
#app,
.v-application {
  font-family: $gmf-font;
  color: #475569;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body,
p,
label,
input,
textarea,
select,
button,
table,
th,
td,
.btn,
.v-btn,
.v-label,
.v-input,
.form-control,
.card,
.modal,
.subheader,
.header,
.breadcrumb,
.biz-header-title {
  font-family: $gmf-font !important;
}

h1, h2, h3, h4, h5, h6,
.card-title,
.modal-title,
.v-card__title {
  font-family: $gmf-font !important;
  font-weight: 600;
  color: #16124a;
  letter-spacing: -0.02em;
}

i.mdi,
.mdi::before {
  font-family: "Material Design Icons" !important;
}

#kt_aside.gmf-aside,
#kt_aside.gmf-aside *:not(i):not(.mdi):not([class^="flaticon"]):not([class*=" flaticon"]):not([class^="flaticon2"]) {
  font-family: $gmf-font !important;
}

.font-poppins,
.font-poppins-light,
.font-poppins-regular,
.font-poppins-medium,
.font-poppins-semibold,
.font-poppins-bold,
.font-poppins-extrabold,
.font-poppins-black {
  font-family: $gmf-font !important;
}

.font-poppins-light { font-weight: 400 !important; }
.font-poppins-regular { font-weight: 400 !important; }
.font-poppins-medium { font-weight: 500 !important; }
.font-poppins-semibold { font-weight: 600 !important; }
.font-poppins-bold { font-weight: 700 !important; }
.font-poppins-extrabold { font-weight: 700 !important; }
.font-poppins-black { font-weight: 700 !important; }

// 3rd party plugins css
@import "~bootstrap-vue/dist/bootstrap-vue.css";
@import "~perfect-scrollbar/css/perfect-scrollbar.css";
@import "~socicon/css/socicon.css";
@import "~animate.css";
@import "~@fortawesome/fontawesome-free/css/all.css";
@import "~line-awesome/dist/line-awesome/css/line-awesome.css";
@import "assets/plugins/flaticon/flaticon.css";
@import "assets/plugins/flaticon2/flaticon.css";
@import "assets/plugins/keenthemes-icons/font/ki.css";

// Main demo style scss
@import "assets/sass/style.vue";
@import "assets/sass/gmf-tailwind-shell.scss";

// Check documentation for RTL css
// Update HTML with RTL attribute at public/index.html
/*@import "assets/css/style.vue.rtl";*/

/* Global Theme Improvements */
:root {
  --gmf-primary: #241B5A;
  --gmf-brand-50: #eef0ff;
  --gmf-brand-100: #e0e7ff;
  --gmf-brand-600: #241B5A;
  --gmf-brand-900: #16124a;
  --gmf-canvas: #faf8f6;
  --gmf-sidebar-start: #241B5A;
  --gmf-sidebar-end: #14132F;
  --primary-gradient: #241B5A;
  --primary-shadow: 0 4px 6px rgba(36, 27, 90, 0.25);
  --secondary-gradient: #241B5A;
}

/* Enhanced Header Styling */
.header {
  border-bottom: 1px solid #e8e8ee;
}

/* Enhanced Sidebar Styling */
.aside {
  border-right: 1px solid color-mix(in srgb, #e0e7ff 80%, transparent);
}

/* Primary CTAs match sidebar background */
.btn-primary,
.btn.btn-primary,
.v-btn.bg-primary,
.bg-primary {
  background: var(--primary-gradient) !important;
  background-color: #241B5A !important;
  border: none !important;
  border-color: #241B5A !important;
  color: #ffffff !important;
  box-shadow: var(--primary-shadow) !important;
  transition: all 0.3s ease !important;
}

.btn-primary:hover,
.btn-primary:focus,
.btn-primary:active,
.v-btn.bg-primary:hover,
.bg-primary:hover {
  background: linear-gradient(180deg, #2B2558 0%, #1a1740 100%) !important;
  background-color: #2B2558 !important;
  border-color: #2B2558 !important;
  color: #ffffff !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 6px 25px rgba(36, 27, 90, 0.45) !important;
}

.text-primary {
  color: #241B5A !important;
}

/* Order Details Modal Body Padding - Global Override */
html body #order-details-modal__BV_modal_body_,
html body #order-details-modal .modal-body,
html body .modal.show #order-details-modal .modal-body,
html body div[id*="order-details-modal__BV_modal_body_"],
html body .modal-body[id*="order-details-modal"],
#order-details-modal__BV_modal_body_,
#order-details-modal .modal-body,
body #order-details-modal .modal-body,
.modal.show #order-details-modal .modal-body,
div[id*="order-details-modal__BV_modal_body_"],
.modal-body[id*="order-details-modal"] {
  padding: 0rem !important;
}

/* Enhanced Card Styling */
.card {
  border-radius: 12px !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s ease !important;
}

.card:hover {
  transform: none !important;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08) !important;
}

/* Improved Form Controls */
.form-control:focus {
  border-color: #241B5A !important;
  box-shadow: 0 0 0 0.2rem rgba(36, 27, 90, 0.25) !important;
}

/* Enhanced Navigation — do not shift sidebar / header links */
.content .nav-link {
  transition: color 0.2s ease, background 0.2s ease !important;
}

/* Improved Loading States */
.spinner-border {
  color: #241B5A !important;
}

/* Enhanced Modal Styling */
.modal-content {
  border-radius: 16px !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
  border: none !important;
}

/* Improved Table Styling */
.table {
  border-radius: 12px !important;
  overflow: hidden !important;
}

.table thead th {
  background: var(--primary-gradient) !important;
  color: white !important;
  border: none !important;
  font-weight: 600 !important;
}

/* Enhanced Pagination */
.page-link {
  border-radius: 8px !important;
  margin: 0 2px !important;
  border: 1px solid #e9ecef !important;
  transition: all 0.3s ease !important;
}

.page-link:hover {
  background: var(--primary-gradient) !important;
  color: white !important;
  transform: translateY(-1px) !important;
}

.page-item.active .page-link {
  background: var(--primary-gradient) !important;
  border-color: #241B5A !important;
}
</style>

<script>
import { OVERRIDE_LAYOUT_CONFIG } from "@/core/services/store/config.module";
import { UPDATE_AUTH } from "@/core/services/store/auth.module";
import AppLoader from "@/components/AppLoader.vue";

export default {
  name: "GetMeFood",
  components: {
    AppLoader
  },
  data() {
    return {
      isAppLoading: true
    };
  },
  mounted() {
    /* Update user state from session storage*/

    // let userAuth = sessionStorage.getItem('USER_STATE');
    // if (userAuth) {

    //   this.$store.dispatch(UPDATE_AUTH);
    // }

    /**
     * this is to override the layout config using saved data from localStorage
     * remove this to use config only from static json (@/core/config/layout.config.json)
     */
    this.$store.dispatch(OVERRIDE_LAYOUT_CONFIG);
    
    // Hide loader after app is ready
    this.hideLoader();
  },
  methods: {
    hideLoader() {
      // Brief boot splash only — avoid feeling stuck on gray/logo screen
      setTimeout(() => {
        this.isAppLoading = false;
      }, 600);
    }
  }
};
</script>
